import { Fragment, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { T } from "../../ui/tokens";
import { fmt, fmtDate } from "../../lib/format";
import { useFaturasPendentes, type TipoPendente } from "./useFaturasPendentes";
import type { FaturaPendente } from "./types";

interface Props {
  empresa?: string;
  tipo: TipoPendente;
}

// Formata "2026-04" -> "Abril 2026".
function mesLabel(dataIso: string | null): string {
  if (!dataIso) return "—";
  const d = new Date(dataIso.slice(0, 10) + "T00:00:00");
  if (Number.isNaN(d.getTime())) return dataIso.slice(0, 7);
  const s = d.toLocaleDateString("pt-PT", { month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function FaturasPendentesTab({ empresa, tipo }: Props) {
  const { data, isLoading, isError } = useFaturasPendentes(empresa, tipo);
  const rotulo = tipo === "cliente" ? "Faturas Clientes" : "Faturas Fornecedores";
  const [filtro, setFiltro] = useState("");

  // Pesquisa inteligente: por fornecedor/cliente, número do documento, OU
  // valor (se o que se escreveu se ler como número) -- sem isto, uma
  // empresa com 100+ faturas pendentes fica só com scroll puro, impraticável
  // para o contabilista encontrar uma fatura específica.
  const faturasFiltradas = useMemo(() => {
    const todas = data?.faturas || [];
    const f = filtro.trim().toLowerCase();
    if (!f) return todas;
    const valorFiltro = parseFloat(filtro.trim().replace(",", "."));
    const temValorFiltro = !Number.isNaN(valorFiltro);
    return todas.filter((fa: FaturaPendente) =>
      (fa.nome_emitente || "").toLowerCase().includes(f) ||
      (fa.numero_documento || "").toLowerCase().includes(f) ||
      (fa.nif_emitente || "").includes(f) ||
      (temValorFiltro && Math.abs(Math.abs(fa.valor_total || 0) - Math.abs(valorFiltro)) < 0.01)
    );
  }, [data, filtro]);

  if (!empresa) {
    return (
      <div style={{ padding: "64px 24px", textAlign: "center", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Seleciona uma empresa</div>
        <div style={{ fontSize: 13, color: T.textMid, marginTop: 4 }}>{rotulo} são filtradas só por empresa — escolhe uma para veres as pendentes.</div>
      </div>
    );
  }
  if (isError) {
    return <div style={{ padding: "40px 24px", textAlign: "center", color: T.red, fontSize: 13 }}>Não foi possível carregar as {rotulo.toLowerCase()}.</div>;
  }
  if (isLoading || !data) {
    return <div style={{ padding: "40px 24px", textAlign: "center", color: T.textLight, fontSize: 13 }}>A carregar…</div>;
  }

  return (
    <div>
      {/* Resumo — quantas, valor total, desde quando */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, background: T.border, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
        <div style={{ background: T.surface, padding: "14px 18px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.06em" }}>Pendentes</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: data.total > 0 ? T.red : T.text, marginTop: 5 }}>{data.total}</div>
        </div>
        <div style={{ background: T.surface, padding: "14px 18px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.06em" }}>Valor total</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: T.text, marginTop: 5, fontVariantNumeric: "tabular-nums" }}>{fmt(data.valor_total)}</div>
        </div>
        <div style={{ background: T.surface, padding: "14px 18px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.06em" }}>Período</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6 }}>
            {data.total === 0 ? "—" : `${mesLabel(data.periodo_inicio)} → ${mesLabel(data.periodo_fim)}`}
          </div>
        </div>
      </div>

      {data.faturas.length === 0 ? (
        <div style={{ padding: "48px 24px", textAlign: "center", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, color: T.textLight, fontSize: 13 }}>
          Nada pendente — todas as {rotulo.toLowerCase()} desta empresa estão reconciliadas.
        </div>
      ) : (
        <>
          <div style={{ position: "relative", marginBottom: 12 }}>
            <Search size={14} color={T.textLight} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
            <input
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder="Procurar por fornecedor/cliente, número do documento ou valor…"
              style={{ width: "100%", padding: "9px 12px 9px 30px", borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 13, color: T.text, background: T.surface, outline: "none", boxSizing: "border-box" }}
            />
          </div>

          {faturasFiltradas.length === 0 ? (
            <div style={{ padding: "40px 24px", textAlign: "center", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, color: T.textLight, fontSize: 13 }}>
              Nenhuma fatura encontrada para "{filtro}".
            </div>
          ) : (
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
              {filtro && (
                <div style={{ padding: "8px 14px", fontSize: 11.5, color: T.textLight, background: T.bg, borderBottom: `1px solid ${T.border}` }}>
                  A mostrar {faturasFiltradas.length} de {data.faturas.length}
                </div>
              )}
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Documento", tipo === "cliente" ? "Cliente" : "Fornecedor", "Data", "Pagamento", "Valor"].map((h) => (
                      <th key={h} style={{ padding: "9px 14px", textAlign: h === "Valor" ? "right" : "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.textLight, background: T.bg, borderBottom: `1px solid ${T.border}` }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {faturasFiltradas.map((f: FaturaPendente, i: number) => {
                    const mesAtual = (f.data_documento || "").slice(0, 7);
                    const mesAnterior = i > 0 ? (faturasFiltradas[i - 1].data_documento || "").slice(0, 7) : null;
                    const mudaMes = mesAtual !== mesAnterior;
                    return (
                      <Fragment key={f.id}>
                        {mudaMes && (
                          <tr key={`sep-${mesAtual}`}>
                            <td colSpan={5} style={{ padding: "8px 14px", background: T.bg, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
                              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.textMid }}>{mesLabel(f.data_documento)}</span>
                            </td>
                          </tr>
                        )}
                        <tr key={f.id} style={{ borderTop: mudaMes ? "none" : `1px solid ${T.borderSoft}` }}>
                          <td style={{ padding: "11px 14px", borderLeft: f.eh_nota_credito ? `2px solid ${T.red}` : "2px solid transparent" }}>
                            <div style={{ fontSize: 12.5, fontWeight: 700, color: T.text, fontFamily: "ui-monospace, monospace" }}>{f.numero_documento}</div>
                            {!!f.eh_nota_credito && <div style={{ fontSize: 10.5, color: T.red, marginTop: 2 }}>↘ Nota de Crédito</div>}
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{f.nome_emitente || "—"}</div>
                            {f.nif_emitente && <div style={{ fontSize: 10.5, color: T.textLight, fontFamily: "ui-monospace, monospace" }}>NIF {f.nif_emitente}</div>}
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: 12.5, color: T.textMid, whiteSpace: "nowrap" }}>{fmtDate(f.data_documento)}</td>
                          <td style={{ padding: "11px 14px", minWidth: 170 }}>
                            {(f.valor_pago || 0) > 0 ? <>
                              <div style={{ fontSize: 11.5, fontWeight: 700, color: T.amber }}>{f.percentagem_paga}% pago · {fmt(f.valor_pago)}</div>
                              <div style={{ height: 4, marginTop: 5, borderRadius: 99, background: T.border, overflow: "hidden" }}><div style={{ height: "100%", width: `${Math.min(100, f.percentagem_paga)}%`, background: T.amber }} /></div>
                              <div style={{ fontSize: 10.5, color: T.textLight, marginTop: 3 }}>Em dívida: {fmt(f.valor_em_divida)}</div>
                            </> : <span style={{ fontSize: 11.5, color: T.textLight }}>Por pagar</span>}
                          </td>
                          <td style={{ padding: "11px 14px", textAlign: "right", fontSize: 14, fontWeight: 700, color: f.valor_total < 0 ? T.red : T.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                            {fmt(f.valor_total)}
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
